import { Component, Input, Output, EventEmitter, computed } from '@angular/core';

/**
 * Page navigator that renders a window of page buttons and emits the target page on navigation.
 */
@Component({
  selector: 'es-pagination',
  template: `
    <div class="flex items-center justify-between text-sm">
      <span class="text-[var(--text-secondary)]">
        {{ startItem() }}–{{ endItem() }} von {{ total }}
      </span>
      <div class="flex items-center gap-1">
        <button
          class="px-2 py-1 rounded text-[var(--text-secondary)] hover:text-[var(--text)] hover:bg-[var(--bg-card-hover)] disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
          [disabled]="page <= 1"
          (click)="goTo(page - 1)"
        >◀</button>
        @for (p of visiblePages(); track p) {
          <button
            class="px-2.5 py-1 rounded text-sm cursor-pointer"
            [class]="p === page
              ? 'bg-echo-blue/15 text-echo-blue font-medium'
              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'"
            (click)="goTo(p)"
          >{{ p }}</button>
        }
        <button
          class="px-2 py-1 rounded text-[var(--text-secondary)] hover:text-[var(--text)] hover:bg-[var(--bg-card-hover)] disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
          [disabled]="page >= totalPages()"
          (click)="goTo(page + 1)"
        >▶</button>
      </div>
    </div>
  `,
})
export class PaginationComponent {
  @Input() total = 0;
  @Input() page = 1;
  @Input() pageSize = 20;
  @Output() pageChange = new EventEmitter<number>();

  totalPages = computed(() => Math.ceil(this.total / this.pageSize) || 1);
  startItem = computed(() => (this.page - 1) * this.pageSize + 1);
  endItem = computed(() => Math.min(this.page * this.pageSize, this.total));

  visiblePages = computed(() => {
    const total = this.totalPages();
    const current = this.page;
    const pages: number[] = [];
    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  });

  goTo(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.pageChange.emit(page);
    }
  }
}
